import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import React, { useEffect, useMemo, useState } from "react";
import { ShieldAlert, Plus, Paperclip, Download, Trash2, Pencil, X, ClipboardCheck, Gavel, History as HistoryIcon } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import EntityLink from "../components/EntityLink";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useScopedModuleId } from "../contexts/ModuleScopeContext";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import EmployeePicker from "../components/EmployeePicker";
import { Textarea } from "../components/ui/textarea";
import { TableSkeleton } from "../components/SkeletonHelpers";
import EmptyState from "../components/EmptyState";
import EvidenceUpload from "../components/EvidenceUpload";
import { usePageAction } from "../contexts/UXChromeContext";
import { toast } from "sonner";
import RowActions from "../components/RowActions";
import ShowDeletedToggle, { DeletedPill } from "../components/ShowDeletedToggle";
import { useHasPermission } from "../contexts/PermissionsContext";
import { rowOpenProps } from "../hooks/useRowOpen";
import DateInput from "../components/DateInput";
import ModuleIdChip from "../components/ModuleIdChip";

const TYPE_TONE = {
    verbal_warning: "bg-amber-50 text-amber-700",
    written_warning: "bg-orange-50 text-orange-700",
    final_warning: "bg-rose-50 text-rose-700",
    suspension: "bg-purple-50 text-purple-700",
    termination: "bg-red-100 text-red-800",
};
const STATUS_TONE = {
    awaiting_review: "bg-amber-100 text-amber-800",
    reviewed: "bg-sky-50 text-sky-700",
    issued: "bg-amber-50 text-amber-700",
    acknowledged: "bg-blue-50 text-blue-700",
    appealed: "bg-purple-50 text-purple-700",
    closed: "bg-gray-100 text-gray-600",
    withdrawn: "bg-gray-100 text-gray-500",
};

const STATUS_LABEL = {
    awaiting_review: "awaiting review",
    reviewed: "reviewed",
    issued: "issued",
    acknowledged: "acknowledged",
    appealed: "appealed",
    closed: "closed",
    withdrawn: "withdrawn",
};

const REASONS = ["attendance", "performance", "misconduct", "policy_violation", "safety", "theft", "harassment", "other"];

export default function Discipline() {
    const navigate = useNavigate();
    const { moduleCode: pathModuleCode } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user } = useAuth();
    const scopedId = useScopedModuleId();
    const canCreate = useHasPermission("discipline.write", scopedId || null);
    const canAck = useHasPermission("discipline.acknowledge", scopedId || null);
    const canHR = useHasPermission("discipline.close", scopedId || null);
    const canReview = useHasPermission("discipline.review", scopedId || null);
    // Iter60.p50 — `employees.view` was stripped from Attendance Officer.
    // Skip the /employees fetch when the user lacks the perm; the
    // discipline row already carries `employee_name` server-side.
    const canViewEmployeesList = useHasPermission("employees.view", scopedId || null);

    const initialStatus = searchParams.get("status") || "all";
    const [items, setItems] = useState([]);
    const [awaitingList, setAwaitingList] = useState([]);
    const [detail, setDetail] = useState(null);
    const [detailStack, setDetailStack] = useState([]); // Iter49-fix — back-stack for history navigation
    const [editing, setEditing] = useState(null); // record being edited
    const [deciding, setDeciding] = useState(null); // record awaiting decide action
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState({ status: initialStatus });
    const [showDeleted, setShowDeleted] = useState(false);

    // Open a related case from the history tab — push the current detail onto
    // the back-stack, then load the target case (full record with hydrations)
    // and swap it in.
    const openRelated = async (recId) => {
        try {
            const { data } = await api.get(`/discipline/${recId}`);
            setDetailStack((s) => (detail ? [...s, detail] : s));
            setDetail(data);
        } catch (err) { toast.error(formatApiError(err)); }
    };
    const goBackDetail = () => {
        setDetailStack((s) => {
            if (s.length === 0) { setDetail(null); return s; }
            const next = s.slice(0, -1);
            setDetail(s[s.length - 1]);
            return next;
        });
    };
    const closeDetail = () => {
        setDetail(null);
        setDetailStack([]);
    };

    // Iter60.p55b — support `?open={id}` deep-link so an incident's
    // "Related discipline case" chip can navigate straight into the
    // case detail dialog without a second click.
    useEffect(() => {
        const openId = searchParams.get("open");
        if (!openId || detail?.id === openId) return;
        (async () => {
            try {
                const { data } = await api.get(`/discipline/${openId}`);
                setDetail(data);
            } catch (err) { toast.error(formatApiError(err)); }
        })();
        // Clear the query param after opening so back/forward doesn't
        // keep re-opening it.
        const next = new URLSearchParams(searchParams);
        next.delete("open");
        setSearchParams(next, { replace: true });
    }, [searchParams, detail?.id, setSearchParams]);

    usePageAction(canCreate ? { label: "New Discipline Record", run: () => setOpen(true) } : null, [canCreate]);

    const load = async () => {
        setLoading(true);
        try {
            const params = { page_size: 200 };
            if (scopedId) params.module_id = scopedId;
            if (filter.status !== "all") params.status = filter.status;
            if (showDeleted) params.include_deleted = true;
            const calls = [
                api.get("/discipline", { params }),
            ];
            if (canViewEmployeesList) {
                calls.push(api.get("/employees", { params: { module_id: scopedId, page_size: 200 } }));
            } else {
                // Placeholder so array destructuring stays stable.
                calls.push(Promise.resolve({ data: { items: [] } }));
            }
            // Pinned "Awaiting my review" list — only when the user actually
            // reviews cases. Cross-module for HR/Admin; module_scope_filter on
            // the server handles dept mgr scoping.
            if (canReview) {
                const awaitingParams = { status: "awaiting_review", page_size: 50 };
                if (scopedId) awaitingParams.module_id = scopedId;
                calls.push(api.get("/discipline", { params: awaitingParams }));
            }
            const [r, e, aw] = await Promise.all(calls);
            setItems(r.data.items || []);
            setEmployees(e.data.items || e.data || []);
            setAwaitingList(canReview ? (aw?.data.items || []) : []);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [scopedId, filter.status, showDeleted, canReview]);

    // Keep filter.status in sync with URL query param (e.g. from dashboard link)
    useEffect(() => {
        const s = searchParams.get("status") || "all";
        if (s !== filter.status) setFilter((f) => ({ ...f, status: s }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const empMap = useMemo(() => Object.fromEntries(employees.map(e => [e.id, e])), [employees]);

    const ack = async (id) => {
        const at = new Date().toISOString();
        try {
            await api.post(`/discipline/${id}/acknowledge`, { signed_by_employee_at: at });
            toast.success("Acknowledged");
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
    };
    const close_ = async (id) => {
        try {
            await api.post(`/discipline/${id}/close`, { note: "" });
            toast.success("Closed");
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <section className="space-y-4" data-testid="discipline-page">
            {canReview && (
                <AwaitingReviewPanel
                    items={awaitingList}
                    empMap={empMap}
                    onOpen={(r) => setDetail(r)}
                    testid="disc-awaiting-review-panel"
                />
            )}

            <div className="flex items-center justify-between">
                <Select
                    value={filter.status}
                    onValueChange={(v) => {
                        setFilter({ ...filter, status: v });
                        const next = new URLSearchParams(searchParams);
                        if (v === "all") next.delete("status"); else next.set("status", v);
                        setSearchParams(next, { replace: true });
                    }}
                >
                    <SelectTrigger className="h-9 w-52" data-testid="disc-status-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        <SelectItem value="awaiting_review">Awaiting review</SelectItem>
                        <SelectItem value="reviewed">Reviewed (awaiting decision)</SelectItem>
                        <SelectItem value="issued">Issued</SelectItem>
                        <SelectItem value="acknowledged">Acknowledged</SelectItem>
                        <SelectItem value="appealed">Appealed</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                        <SelectItem value="withdrawn">Withdrawn</SelectItem>
                    </SelectContent>
                </Select>
                {canCreate && (
                    <Button onClick={() => setOpen(true)} className="asl-navy-bg text-white" data-testid="disc-new-btn">
                        <Plus className="h-4 w-4 mr-1" /> New Record
                    </Button>
                )}
                <ShowDeletedToggle value={showDeleted} onChange={setShowDeleted} testid="disc-show-deleted" />
            </div>

            <div className="asl-card overflow-hidden">
                {loading ? <TableSkeleton rows={5} cols={7} testid="disc-loading" />
                : items.length === 0 ? (
                    <EmptyState
                        icon={ShieldAlert}
                        title={filter.status !== "all" ? "Nothing matches these filters. Try widening the range or clearing a filter." : "No discipline records"}
                        description={filter.status !== "all" ? "Adjust filters or clear them." : "Issue a warning or record an incident to start tracking."}
                        ctaLabel={canCreate ? "New Discipline Record" : undefined}
                        onCta={canCreate ? () => setOpen(true) : undefined}
                        filtered={filter.status !== "all"}
                        onClear={() => setFilter({ status: "all" })}
                        testid="disc-empty"
                    />
                ) : (
                    <table className="asl-table asl-table--zebra" data-testid="disc-table">
                        <thead className="text-xs text-[color:var(--asl-muted)] uppercase">
                            <tr><th className="px-4 py-3 text-left">Employee</th><th className="px-4 py-3 text-left">Type</th>
                            <th className="px-4 py-3 text-left">Reason</th><th className="px-4 py-3 text-left">Summary</th>
                            <th className="px-4 py-3 text-left">Incident</th><th className="px-4 py-3 text-left">Status</th>
                            <th className="px-4 py-3 text-right">Actions</th></tr>
                        </thead>
                        <tbody>
                            {items.map(r => (
                                <tr key={r.id} {...rowOpenProps(navigate, () => setDetail(r), { testid: `disc-row-${r.id}`, className: 'border-t' })} style={{ borderColor: "var(--asl-border)" }}>
                                    <td className="px-4 py-3">
                                        {r.employee_id ? (
                                            <EntityLink to={`/m/${pathModuleCode}/staff/${r.employee_id}`} testid={`disc-emp-link-${r.id}`}>
                                                <span className="inline-flex items-center gap-2">
                                                    <span>{r.employee_name || r.employee_display || empMap[r.employee_id]?.full_name || "—"}</span>
                                                    <ModuleIdChip
                                                        value={r.employee_module_employee_id || empMap[r.employee_id]?.module_employee_id}
                                                        testid={`disc-emp-mid-${r.id}`}
                                                    />
                                                </span>
                                            </EntityLink>
                                        ) : (
                                            <span className="inline-flex items-center gap-2">
                                                <span>{r.employee_name || r.employee_display || "—"}</span>
                                                <ModuleIdChip
                                                    value={r.employee_module_employee_id}
                                                    testid={`disc-emp-mid-${r.id}`}
                                                />
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3"><span className={`text-[10px] uppercase font-bold rounded-full px-2 py-0.5 ${TYPE_TONE[r.record_type] || "bg-gray-100"}`}>{(r.record_type || "unknown").replace(/_/g, " ")}</span></td>
                                    <td className="px-4 py-3">{r.reason_category}</td>
                                    <td className="px-4 py-3 max-w-md truncate">{r.reason_summary}</td>
                                    <td className="px-4 py-3 mono text-xs">{r.incident_date}</td>
                                    <td className="px-4 py-3"><span className={`text-[10px] uppercase font-bold rounded-full px-2 py-0.5 ${STATUS_TONE[r.status] || "bg-gray-100"}`}>{STATUS_LABEL[r.status] || r.status}</span></td>
                                    <td className="px-4 py-3 text-right space-x-1" data-no-row-click>
                                        {r.status === "awaiting_review" && canReview && (
                                            <Button size="sm" variant="outline" onClick={() => setDetail(r)} data-testid={`disc-review-${r.id}`}>
                                                <ClipboardCheck className="h-3.5 w-3.5 mr-1" /> Review
                                            </Button>
                                        )}
                                        {r.status === "reviewed" && canCreate && (
                                            <Button size="sm" variant="outline" onClick={() => setDeciding(r)} data-testid={`disc-decide-${r.id}`}>
                                                <Gavel className="h-3.5 w-3.5 mr-1" /> Record decision
                                            </Button>
                                        )}
                                        {r.status === "issued" && canAck && (
                                            <Button size="sm" variant="outline" onClick={() => ack(r.id)} data-testid={`disc-ack-${r.id}`}>Acknowledge</Button>
                                        )}
                                        {["issued", "acknowledged"].includes(r.status) && canCreate && (
                                            <Button size="sm" variant="ghost" onClick={() => setEditing(r)} data-testid={`disc-edit-${r.id}`}>
                                                <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                                            </Button>
                                        )}
                                        {!["closed", "withdrawn"].includes(r.status) && canHR && (
                                            <Button size="sm" variant="ghost" onClick={() => close_(r.id)} data-testid={`disc-close-${r.id}`}>Close</Button>
                                        )}
                                        {r.deleted_at && <DeletedPill deletedAt={r.deleted_at} />}
                                        <RowActions
                                            entity="discipline"
                                            endpoint="/discipline"
                                            id={r.id}
                                            label={r.id.slice(0, 8)}
                                            deleted={!!r.deleted_at}
                                            moduleId={r.module_id}
                                            hardDeleteSupported={false}
                                            onChange={load}
                                            testid={`disc-actions-${r.id}`}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <DiscFormDialog open={open} onOpenChange={setOpen} onSaved={load} employees={employees} moduleId={scopedId} />
            <DiscFormDialog
                open={!!editing}
                onOpenChange={(o) => { if (!o) setEditing(null); }}
                onSaved={load}
                employees={employees}
                initial={editing}
                moduleId={scopedId}
            />
            <DecideDialog
                record={deciding}
                onClose={() => setDeciding(null)}
                onSaved={async () => { setDeciding(null); await load(); }}
            />
            <DiscDetailDialog
                record={detail}
                onClose={closeDetail}
                onGoBack={detailStack.length > 0 ? goBackDetail : null}
                stackDepth={detailStack.length}
                onOpenRelated={openRelated}
                onEdit={(r) => { closeDetail(); setEditing(r); }}
                onAck={ack}
                onClose_={close_}
                canAck={canAck}
                canHR={canHR}
                canWrite={canCreate}
                canReview={canReview}
                empMap={empMap}
                pathModuleCode={pathModuleCode}
                onDecide={(r) => { closeDetail(); setDeciding(r); }}
                onRefresh={async () => {
                    // Reload the record so newly-added/removed attachments show.
                    await load();
                    if (detail) {
                        try {
                            const { data } = await api.get(`/discipline/${detail.id}`);
                            setDetail(data);
                        } catch (e) { /* ignore */ }
                    }
                }}
            />
        </section>
    );
}

function DiscFormDialog({ open, onOpenChange, onSaved, employees, initial, moduleId }) {
    const today = new Date().toISOString().slice(0, 10);
    const isEdit = !!initial;
    const [form, setForm] = useState({
        employee_id: "", record_type: "verbal_warning", reason_category: "attendance",
        reason_summary: "", incident_date: today, effective_date: "", notes: "", evidence_url: null,
        suspension_days: 1, termination_effective_date: today, closure_note: "",
    });
    useEffect(() => {
        if (!open) return;
        if (initial) {
            setForm({
                employee_id: initial.employee_id || "",
                record_type: initial.record_type || "verbal_warning",
                reason_category: initial.reason_category || "attendance",
                reason_summary: initial.reason_summary || "",
                incident_date: initial.incident_date || today,
                effective_date: initial.effective_date || "",
                notes: initial.notes || "",
                evidence_url: initial.evidence_url || null,
                suspension_days: initial.suspension_days || 1,
                termination_effective_date: initial.termination_effective_date || today,
                closure_note: initial.closure_note || "",
            });
        } else {
            setForm({
                employee_id: "", record_type: "verbal_warning", reason_category: "attendance",
                reason_summary: "", incident_date: today, effective_date: "", notes: "", evidence_url: null,
                suspension_days: 1, termination_effective_date: today, closure_note: "",
            });
        }
    }, [open, initial, today]);
    const isSusp = form.record_type === "suspension";
    const isTerm = form.record_type === "termination";
    const submit = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...form };
            if (!isSusp) delete payload.suspension_days;
            if (!isTerm) delete payload.termination_effective_date;
            if (!payload.effective_date) delete payload.effective_date;
            if (isEdit) {
                // PATCH — employee_id is immutable so omit it.
                delete payload.employee_id;
                await api.patch(`/discipline/${initial.id}`, payload);
                toast.success("Record updated");
            } else {
                await api.post("/discipline", payload);
                toast.success("Record created");
            }
            onOpenChange(false);
            await onSaved();
        } catch (err) { toast.error(formatApiError(err)); }
    };
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid={isEdit ? "disc-edit-dialog" : "disc-new-dialog"}>
                <DialogHeader><DialogTitle>{isEdit ? "Edit Discipline Record" : "New Discipline Record"}</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3 pt-1">
                    <div>
                        <Label className="text-xs uppercase">Employee</Label>
                        {isEdit ? (
                            <div className="h-9 px-3 flex items-center rounded-md bg-gray-50 text-sm text-[color:var(--asl-navy)]" data-testid="disc-emp-immutable">
                                {(employees.find((e) => e.id === form.employee_id) || {}).full_name || "—"}
                                <span className="ml-2 text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">immutable</span>
                            </div>
                        ) : (
                            /* Iter60.p15 / p15.1 — shared EmployeePicker:
                               server-side scope + search, no auto-select. */
                            <EmployeePicker
                                moduleId={moduleId}
                                employees={employees}
                                value={form.employee_id}
                                onChange={(id) => setForm({ ...form, employee_id: id })}
                                placeholder="Pick employee"
                                testid="disc-emp-picker"
                            />
                        )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs uppercase">Type</Label>
                            <Select value={form.record_type} onValueChange={(v) => setForm({ ...form, record_type: v })}>
                                <SelectTrigger data-testid="disc-type-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="verbal_warning">Verbal warning</SelectItem>
                                    <SelectItem value="written_warning">Written warning</SelectItem>
                                    <SelectItem value="final_warning">Final warning</SelectItem>
                                    <SelectItem value="suspension">Suspension</SelectItem>
                                    <SelectItem value="termination">Termination</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs uppercase">Reason</Label>
                            <Select value={form.reason_category} onValueChange={(v) => setForm({ ...form, reason_category: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>{REASONS.map(r => <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div><Label className="text-xs uppercase">Summary</Label><Input value={form.reason_summary} onChange={(e) => setForm({ ...form, reason_summary: e.target.value })} required data-testid="disc-summary-input" /></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label className="text-xs uppercase">Incident date</Label><DateInput value={form.incident_date} onChange={(v) => setForm({ ...form, incident_date: v })} /></div>
                        <div><Label className="text-xs uppercase">Effective date</Label><DateInput value={form.effective_date} onChange={(v) => setForm({ ...form, effective_date: v })} /></div>
                        {isSusp && <div><Label className="text-xs uppercase">Suspension days</Label><Input type="number" min="1" value={form.suspension_days} onChange={(e) => setForm({ ...form, suspension_days: parseInt(e.target.value, 10) })} /></div>}
                        {isTerm && <div><Label className="text-xs uppercase">Termination effective</Label><DateInput value={form.termination_effective_date} onChange={(v) => setForm({ ...form, termination_effective_date: v })} /></div>}
                    </div>
                    <div><Label className="text-xs uppercase">Notes</Label><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                    <div><Label className="text-xs uppercase">Closure note</Label><Textarea rows={2} value={form.closure_note} onChange={(e) => setForm({ ...form, closure_note: e.target.value })} placeholder="Optional — added when closing or as a summary." /></div>
                    {!isEdit && <EvidenceUpload value={form.evidence_url} onChange={(url) => setForm({ ...form, evidence_url: url })} testid="disc-evidence" />}
                    {(isSusp || isTerm) && (
                        <div className="text-[11px] asl-crimson-text bg-rose-50 px-3 py-2 rounded">
                            ⚠ Suspension/Termination affects payroll. Employee status flip is queued for the next daily worker run (manual flip required this iteration).
                        </div>
                    )}
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                        <Button type="submit" className="asl-navy-bg text-white" data-testid="disc-submit">{isEdit ? "Save changes" : "Create"}</Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}


function DiscDetailDialog({ record, onClose, onGoBack, stackDepth, onOpenRelated, onEdit, onAck, onClose_, canAck, canHR, canWrite, canReview, empMap, pathModuleCode, onRefresh, onDecide }) {
    if (!record) return null;
    const emp = empMap[record.employee_id];
    const activeAttachments = (record.attachments || []).filter((a) => !a.deleted_at);
    const isEditable = ["issued", "acknowledged"].includes(record.status);
    // Iter49-fix — Record decision button is ALWAYS visible in the footer when
    // the user has decide permission; disabled + tooltip when the case is not
    // yet `reviewed` so the affordance is discoverable.
    const canDecide = canWrite && !record.decided_at && !["closed", "withdrawn"].includes(record.status);
    const decideEnabled = record.status === "reviewed";
    const decideTooltip = record.status === "awaiting_review"
        ? "Waiting for Kaafi HR or Admin to record their recommendation before a final decision can be made."
        : record.status === "reviewed"
            ? "Record the final action for this case"
            : `Cannot record a decision from status ${record.status.replace(/_/g, " ")}`;
    return (
        <Dialog open={!!record} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-2xl" data-testid="disc-detail-dialog">
                <DialogHeader>
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                            {onGoBack && (
                                <button
                                    type="button"
                                    onClick={onGoBack}
                                    className="text-[11px] mono asl-navy-text hover:underline flex items-center gap-1 mb-1"
                                    data-testid="disc-detail-back"
                                >
                                    ← Back to previous case{stackDepth > 1 ? ` (${stackDepth} in history)` : ""}
                                </button>
                            )}
                            <DialogTitle className="flex items-center gap-2 flex-wrap">
                                <span className={`text-[10px] uppercase font-bold rounded-full px-2 py-0.5 ${TYPE_TONE[record.record_type] || "bg-gray-100"}`}>{(record.record_type || "").replace(/_/g, " ")}</span>
                                <span>{emp?.full_name || record.employee_name || record.employee_display || "—"}</span>
                                <ModuleIdChip
                                    value={emp?.module_employee_id || record.employee_module_employee_id}
                                    testid={`disc-detail-mid-${record.id}`}
                                />
                            </DialogTitle>
                            <div className="text-xs text-[color:var(--asl-muted)] mt-1 mono">{record.incident_date}</div>
                        </div>
                        <span className={`text-[10px] uppercase font-bold rounded-full px-2 py-0.5 ${STATUS_TONE[record.status] || "bg-gray-100"}`}>{STATUS_LABEL[record.status] || record.status}</span>
                    </div>
                </DialogHeader>

                <Tabs defaultValue="details" className="w-full">
                    <TabsList className="grid w-full grid-cols-3" data-testid="disc-detail-tabs">
                        <TabsTrigger value="details" data-testid="disc-tab-details">Details</TabsTrigger>
                        <TabsTrigger value="review" data-testid="disc-tab-review">Kaafi Review</TabsTrigger>
                        <TabsTrigger value="history" data-testid="disc-tab-history">Employee history</TabsTrigger>
                    </TabsList>

                    <TabsContent value="details">
                        <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-1" data-testid="disc-tab-details-body">
                            <div className="grid grid-cols-2 gap-3 text-xs">
                                <Field label="Reason category">{record.reason_category}</Field>
                                <Field label="Effective date">{record.effective_date || "—"}</Field>
                                <Field label="Suspension days">{record.suspension_days ?? "—"}</Field>
                                <Field label="Termination effective">{record.termination_effective_date || "—"}</Field>
                            </div>
                            <Field label="Summary">{record.reason_summary || "—"}</Field>
                            <Field label="Notes">{record.notes || "—"}</Field>
                            {record.closure_note && <Field label="Closure note">{record.closure_note}</Field>}

                            {/* Evidence & Attachments */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)] flex items-center gap-2">
                                        <Paperclip className="h-3.5 w-3.5" /> Evidence & attachments
                                    </div>
                                    {canWrite && isEditable && (
                                        <AttachButton recordId={record.id} onUploaded={onRefresh} />
                                    )}
                                </div>
                                {activeAttachments.length === 0 ? (
                                    <div className="text-xs text-[color:var(--asl-muted)] italic px-1 py-3" data-testid="disc-attachments-empty">
                                        No attachments yet.
                                    </div>
                                ) : (
                                    <ul className="space-y-1" data-testid="disc-attachments-list">
                                        {activeAttachments.map((a) => (
                                            <li key={a.id} className="flex items-center justify-between text-xs border rounded-md px-2 py-1.5"
                                                style={{ borderColor: "var(--asl-border)" }} data-testid={`disc-attachment-${a.id}`}>
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-medium truncate">{a.filename}</div>
                                                    <div className="text-[10px] text-[color:var(--asl-muted)]">
                                                        {(a.size ? `${Math.round(a.size / 1024)} KB · ` : "")}{a.uploaded_at?.slice(0, 10)}{a.uploaded_by_email ? ` · ${a.uploaded_by_email}` : ""}                                                    </div>
                                                </div>
                                                <a href={a.url} target="_blank" rel="noopener noreferrer" title="Download"
                                                   className="p-1 hover:bg-gray-100 rounded"
                                                   data-testid={`disc-attachment-download-${a.id}`}>
                                                    <Download className="h-3.5 w-3.5" />
                                                </a>
                                                {canWrite && isEditable && (
                                                    <button
                                                        type="button"
                                                        onClick={async () => {
                                                            try {
                                                                await api.delete(`/discipline/${record.id}/attachments/${a.id}`);
                                                                toast.success("Attachment removed");
                                                                onRefresh();
                                                            } catch (e) { toast.error(formatApiError(e)); }
                                                        }}
                                                        className="p-1 hover:bg-rose-50 text-rose-600 rounded ml-1"
                                                        title="Remove"
                                                        data-testid={`disc-attachment-remove-${a.id}`}
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </button>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            {/* Iter60.p55b — reverse-link section:
                                every incident that references this
                                discipline case via
                                `linked_discipline_case_id`. Renders
                                inline in the Details tab because it's
                                a lightweight cross-link, not a
                                full-blown history. */}
                            <RelatedIncidentsSection recordId={record.id} />
                        </div>
                    </TabsContent>

                    <TabsContent value="review">
                        <div className="max-h-[55vh] overflow-y-auto pr-1" data-testid="disc-tab-review-body">
                            <ReviewPanel record={record} canReview={canReview} canWrite={canWrite} onSaved={onRefresh} />
                        </div>
                    </TabsContent>

                    <TabsContent value="history">
                        <div className="max-h-[55vh] overflow-y-auto pr-1" data-testid="disc-tab-history-body">
                            <HistoryPanel record={record} empMap={empMap} pathModuleCode={pathModuleCode} onOpenRelated={onOpenRelated} />
                        </div>
                    </TabsContent>
                </Tabs>

                <DialogFooter>
                    <Button variant="ghost" onClick={onClose} data-testid="disc-detail-close-btn"><X className="h-4 w-4 mr-1" />Close</Button>
                    {canWrite && isEditable && (
                        <Button variant="outline" onClick={() => onEdit(record)} data-testid="disc-detail-edit">
                            <Pencil className="h-4 w-4 mr-1" /> Edit
                        </Button>
                    )}
                    {record.status === "issued" && canAck && (
                        <Button variant="outline" onClick={async () => { await onAck(record.id); onClose(); }} data-testid="disc-detail-ack">
                            Acknowledge
                        </Button>
                    )}
                    {canDecide && (
                        <Button
                            className={decideEnabled ? "asl-navy-bg text-white" : ""}
                            variant={decideEnabled ? "default" : "outline"}
                            disabled={!decideEnabled}
                            title={decideTooltip}
                            onClick={() => decideEnabled && onDecide(record)}
                            data-testid="disc-detail-decide"
                        >
                            <Gavel className="h-4 w-4 mr-1" /> Record decision
                        </Button>
                    )}
                    {!["closed", "withdrawn"].includes(record.status) && canHR && (
                        <Button variant="outline" onClick={async () => { await onClose_(record.id); onClose(); }} data-testid="disc-detail-close-record">
                            Close record
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function Field({ label, children }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">{label}</div>
            <div className="text-sm text-[color:var(--asl-navy)] mt-0.5">{children}</div>
        </div>
    );
}


function AttachButton({ recordId, onUploaded }) {
    const [busy, setBusy] = useState(false);
    const onPick = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data: uploaded } = await api.post("/uploads/document", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            await api.post(`/discipline/${recordId}/attachments`, {
                filename: uploaded.filename || file.name,
                url: uploaded.document_url,
                size: uploaded.size ?? file.size,
                mime_type: file.type,
            });
            toast.success("File attached");
            onUploaded();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
            e.target.value = "";
        }
    };
    return (
        <label className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border cursor-pointer hover:bg-gray-50"
               style={{ borderColor: "var(--asl-border)" }}
               data-testid="disc-attach-btn">
            <Paperclip className="h-3.5 w-3.5" />
            {busy ? "Uploading…" : "+ Attach file"}
            <input type="file" className="hidden" onChange={onPick} disabled={busy}
                   accept=".pdf,.docx,.png,.jpg,.jpeg" data-testid="disc-attach-input" />
        </label>
    );
}


// ---------------------------------------------------------------------------
// Iter49 — Kaafi Review workflow UI
// ---------------------------------------------------------------------------

function AwaitingReviewPanel({ items, empMap, onOpen, testid }) {
    if (!items || items.length === 0) return null;
    return (
        <div className="asl-card p-4 border-l-4" style={{ borderLeftColor: "var(--asl-crimson)" }} data-testid={testid}>
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <ClipboardCheck className="h-4 w-4 asl-crimson-text" />
                    <h3 className="font-bold asl-navy-text uppercase tracking-wide text-sm">
                        Awaiting my review
                    </h3>
                    <span className="text-[10px] uppercase font-bold rounded-full px-2 py-0.5 bg-amber-100 text-amber-800 mono"
                        data-testid="disc-awaiting-count">
                        {items.length}
                    </span>
                </div>
                <span className="text-[11px] text-[color:var(--asl-muted)]">
                    Kaafi HR & Admin review required before a final decision can be recorded.
                </span>
            </div>
            <ul className="space-y-1.5">
                {items.map((r) => {
                    const emp = empMap[r.employee_id];
                    return (
                        <li
                            key={r.id}
                            className="flex items-center justify-between text-xs border rounded-md px-3 py-2 hover:bg-amber-50 cursor-pointer"
                            style={{ borderColor: "var(--asl-border)" }}
                            onClick={() => onOpen(r)}
                            data-testid={`disc-awaiting-item-${r.id}`}
                        >
                            <div className="flex-1 min-w-0">
                                <div className="font-medium asl-navy-text truncate inline-flex items-center gap-2 flex-wrap">
                                    <span>{emp?.full_name || r.employee_name || r.employee_display || "—"}</span>
                                    <ModuleIdChip
                                        value={emp?.module_employee_id || r.employee_module_employee_id}
                                        testid={`disc-awaiting-mid-${r.id}`}
                                    />
                                    <span className="ml-1 text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">
                                        {(r.record_type || "").replace(/_/g, " ")}
                                    </span>
                                </div>
                                <div className="text-[11px] text-[color:var(--asl-muted)] truncate">
                                    {r.reason_summary || "—"}
                                </div>
                            </div>
                            <div className="text-[10px] mono text-[color:var(--asl-muted)] mx-3">
                                {r.incident_date}
                            </div>
                            <Button size="sm" variant="outline" className="asl-crimson-text border-rose-200" data-testid={`disc-awaiting-review-btn-${r.id}`}>
                                Review
                            </Button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}


function ReviewPanel({ record, canReview, canWrite, onSaved }) {
    const status = record.status;
    const alreadyReviewed = !!record.reviewed_at;
    const initial = {
        rec: record.review_recommendation || "",
        notes: record.review_notes || "",
    };
    const [form, setForm] = useState(initial);
    const [saving, setSaving] = useState(false);
    // Reset local form when switching records
    useEffect(() => { setForm(initial); /* eslint-disable-next-line */ }, [record.id]);

    const canWriteReview = canReview && status === "awaiting_review";
    const canAmendReview = canReview && status === "reviewed";
    const readOnly = !canWriteReview && !canAmendReview;

    const submit = async (e) => {
        e.preventDefault();
        if (!form.rec.trim()) {
            toast.error("Recommendation is required");
            return;
        }
        setSaving(true);
        try {
            const body = { review_recommendation: form.rec.trim(), review_notes: form.notes };
            if (canAmendReview) {
                await api.patch(`/discipline/${record.id}/review`, body);
                toast.success("Review updated");
            } else {
                await api.post(`/discipline/${record.id}/review`, body);
                toast.success("Review saved");
            }
            await onSaved();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    return (
        <div className="space-y-4" data-testid="disc-review-panel">
            {/* Existing review (visible to everyone once written) */}
            {alreadyReviewed && (
                <div className="rounded-md border p-3 bg-sky-50/40" style={{ borderColor: "var(--asl-border)" }} data-testid="disc-review-existing">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">
                            Kaafi recommendation
                        </div>
                        <div className="text-[10px] text-[color:var(--asl-muted)] mono">
                            {record.reviewed_by_name || "?"} · {(record.reviewed_at || "").slice(0, 16).replace("T", " ")}
                        </div>
                    </div>
                    <div className="text-sm asl-navy-text whitespace-pre-wrap" data-testid="disc-review-rec-text">
                        {record.review_recommendation || "—"}
                    </div>
                    {record.review_notes && (
                        <div className="mt-2 pt-2 border-t text-xs text-[color:var(--asl-muted)] whitespace-pre-wrap" style={{ borderColor: "var(--asl-border)" }} data-testid="disc-review-notes-text">
                            <span className="uppercase tracking-wider text-[10px] mr-2">Notes:</span>{record.review_notes}
                        </div>
                    )}
                </div>
            )}

            {/* Existing final decision */}
            {record.decided_at && (
                <div className="rounded-md border p-3 bg-emerald-50/40" style={{ borderColor: "var(--asl-border)" }} data-testid="disc-decide-existing">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)] flex items-center gap-1">
                            <Gavel className="h-3 w-3" /> Final decision
                        </div>
                        <div className="text-[10px] text-[color:var(--asl-muted)] mono">
                            {record.decided_by_name || "?"} · {(record.decided_at || "").slice(0, 16).replace("T", " ")}
                        </div>
                    </div>
                    <div className="text-sm asl-navy-text whitespace-pre-wrap" data-testid="disc-decide-action-text">
                        {record.final_action || "—"}
                    </div>
                    {record.decision_note && (
                        <div className="mt-2 pt-2 border-t text-xs text-[color:var(--asl-muted)] whitespace-pre-wrap" style={{ borderColor: "var(--asl-border)" }}>
                            <span className="uppercase tracking-wider text-[10px] mr-2">Note:</span>{record.decision_note}
                        </div>
                    )}
                </div>
            )}

            {/* Reviewer form */}
            {(canWriteReview || canAmendReview) && (
                <form onSubmit={submit} className="space-y-3" data-testid="disc-review-form">
                    <div>
                        <Label className="text-xs uppercase">
                            Recommendation <span className="asl-crimson-text">*</span>
                        </Label>
                        <Textarea
                            rows={4}
                            maxLength={1000}
                            value={form.rec}
                            onChange={(e) => setForm({ ...form, rec: e.target.value })}
                            placeholder="Kaafi HR / Admin recommendation for this case…"
                            required
                            data-testid="disc-review-rec-input"
                        />
                        <div className="text-[10px] text-right text-[color:var(--asl-muted)] mono">
                            {form.rec.length}/1000
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs uppercase">Notes (optional)</Label>
                        <Textarea
                            rows={3}
                            maxLength={2000}
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            placeholder="Any additional context for the deciding manager…"
                            data-testid="disc-review-notes-input"
                        />
                        <div className="text-[10px] text-right text-[color:var(--asl-muted)] mono">
                            {(form.notes || "").length}/2000
                        </div>
                    </div>
                    <div className="flex items-center justify-end gap-2">
                        <Button type="submit" className="asl-navy-bg text-white" disabled={saving} data-testid="disc-review-submit">
                            {saving ? "Saving…" : (canAmendReview ? "Update review" : "Save review")}
                        </Button>
                    </div>
                </form>
            )}

            {/* Non-reviewer, awaiting review — helper copy also mirrored on the
                disabled footer button's tooltip so the affordance is discoverable. */}
            {readOnly && status === "awaiting_review" && (
                <div className="rounded-md border border-dashed p-4 text-sm text-[color:var(--asl-muted)]"
                     style={{ borderColor: "var(--asl-border)" }} data-testid="disc-review-waiting">
                    Waiting for Kaafi HR or Admin to record their recommendation before a final decision can be made.
                </div>
            )}
        </div>
    );
}


function HistoryPanel({ record, empMap, pathModuleCode, onOpenRelated }) {
    const [items, setItems] = useState(null);
    const [error, setError] = useState(null);
    useEffect(() => {
        let alive = true;
        (async () => {
            setItems(null);
            setError(null);
            try {
                const { data } = await api.get(`/discipline/employee/${record.employee_id}/history`, {
                    params: { exclude_id: record.id },
                });
                if (alive) setItems(data || []);
            } catch (err) {
                if (alive) setError(formatApiError(err));
            }
        })();
        return () => { alive = false; };
    }, [record.employee_id, record.id]);

    const emp = empMap[record.employee_id];
    if (error) {
        return <div className="text-xs asl-crimson-text px-1 py-3" data-testid="disc-history-error">{error}</div>;
    }
    if (!items) {
        return <div className="text-xs text-[color:var(--asl-muted)] italic px-1 py-3" data-testid="disc-history-loading">Loading history…</div>;
    }
    if (items.length === 0) {
        return (
            <div className="rounded-md border border-dashed p-6 text-center text-sm text-[color:var(--asl-muted)]"
                 style={{ borderColor: "var(--asl-border)" }} data-testid="disc-history-empty">
                <HistoryIcon className="h-5 w-5 mx-auto mb-1 opacity-50" />
                No prior discipline records for {emp?.full_name || "this employee"}.
            </div>
        );
    }
    return (
        <ul className="space-y-1.5" data-testid="disc-history-list">
            {items.map((r) => {
                const handleOpen = () => onOpenRelated && onOpenRelated(r.id);
                return (
                    <li
                        key={r.id}
                        role="button"
                        tabIndex={0}
                        onClick={handleOpen}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleOpen(); } }}
                        className="flex items-center gap-3 border rounded-md px-3 py-2 text-xs cursor-pointer hover:bg-sky-50/40 hover:shadow-sm transition focus:outline-none focus:ring-2 focus:ring-offset-1"
                        style={{ borderColor: "var(--asl-border)" }}
                        data-testid={`disc-history-item-${r.id}`}
                    >
                        <div className="w-20 shrink-0 mono text-[10px] text-[color:var(--asl-muted)]" data-testid={`disc-history-date-${r.id}`}>
                            {r.incident_date || (r.created_at || "").slice(0, 10) || "—"}
                        </div>
                        <div className="w-24 shrink-0">
                            <span className="text-[10px] uppercase font-bold rounded-full px-2 py-0.5 bg-slate-100 text-slate-700" data-testid={`disc-history-module-${r.id}`}>
                                {r.module_code || r.module_display || r.module_name || "—"}
                            </span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <span className={`text-[10px] uppercase font-bold rounded-full px-2 py-0.5 ${TYPE_TONE[r.record_type] || "bg-gray-100"}`}>
                                    {(r.record_type || "").replace(/_/g, " ")}
                                </span>
                                <span className="text-[color:var(--asl-navy)] truncate" data-testid={`disc-history-summary-${r.id}`}>
                                    {r.reason_summary || "—"}
                                </span>
                            </div>
                            {r.final_action && (
                                <div className="text-[11px] text-[color:var(--asl-muted)] truncate mt-0.5" data-testid={`disc-history-final-${r.id}`}>
                                    <span className="uppercase tracking-wider text-[10px] mr-1">Final:</span>{r.final_action}
                                </div>
                            )}
                        </div>
                        <span className={`text-[10px] uppercase font-bold rounded-full px-2 py-0.5 ${STATUS_TONE[r.status] || "bg-gray-100"} shrink-0`} data-testid={`disc-history-status-${r.id}`}>
                            {STATUS_LABEL[r.status] || r.status}
                        </span>
                    </li>
                );
            })}
        </ul>
    );
}


function DecideDialog({ record, onClose, onSaved }) {
    const [form, setForm] = useState({ action: "", note: "" });
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (record) setForm({ action: "", note: "" });
    }, [record?.id]);
    if (!record) return null;

    const submit = async (e) => {
        e.preventDefault();
        if (!form.action.trim()) {
            toast.error("Final action is required");
            return;
        }
        setSaving(true);
        try {
            await api.post(`/discipline/${record.id}/decide`, {
                final_action: form.action.trim(),
                decision_note: form.note,
            });
            toast.success("Decision recorded");
            await onSaved();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={!!record} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-lg" data-testid="disc-decide-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Gavel className="h-4 w-4 asl-crimson-text" />
                        Record final decision
                    </DialogTitle>
                </DialogHeader>
                <div className="rounded-md border p-3 bg-sky-50/40 mb-2" style={{ borderColor: "var(--asl-border)" }}>
                    <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)] mb-1">
                        Kaafi recommendation
                    </div>
                    <div className="text-sm asl-navy-text whitespace-pre-wrap" data-testid="disc-decide-rec-preview">
                        {record.review_recommendation || "—"}
                    </div>
                    {record.review_notes && (
                        <div className="mt-2 pt-2 border-t text-xs text-[color:var(--asl-muted)] whitespace-pre-wrap" style={{ borderColor: "var(--asl-border)" }}>
                            <span className="uppercase tracking-wider text-[10px] mr-2">Notes:</span>{record.review_notes}
                        </div>
                    )}
                </div>
                <form onSubmit={submit} className="space-y-3">
                    <div>
                        <Label className="text-xs uppercase">
                            Final action <span className="asl-crimson-text">*</span>
                        </Label>
                        <Textarea
                            rows={4}
                            maxLength={1000}
                            value={form.action}
                            onChange={(e) => setForm({ ...form, action: e.target.value })}
                            placeholder="e.g. Written warning issued, follow-up in 30 days."
                            required
                            data-testid="disc-decide-action-input"
                        />
                        <div className="text-[10px] text-right text-[color:var(--asl-muted)] mono">{form.action.length}/1000</div>
                    </div>
                    <div>
                        <Label className="text-xs uppercase">Decision note (optional)</Label>
                        <Textarea
                            rows={3}
                            maxLength={2000}
                            value={form.note}
                            onChange={(e) => setForm({ ...form, note: e.target.value })}
                            data-testid="disc-decide-note-input"
                        />
                        <div className="text-[10px] text-right text-[color:var(--asl-muted)] mono">{(form.note || "").length}/2000</div>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                        <Button type="submit" className="asl-navy-bg text-white" disabled={saving} data-testid="disc-decide-submit">
                            {saving ? "Saving…" : "Record decision"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

// -------------------------------------------------------------------------
// Iter60.p55b — RelatedIncidentsSection
// -------------------------------------------------------------------------
// Renders every incident whose `linked_discipline_case_id` matches
// this discipline record. Click → jumps to the module's Incidents
// page with a deep-linked drawer via `?open={incident_id}`. Empty
// state stays silent (no clutter for cases that were never linked).
function RelatedIncidentsSection({ recordId }) {
    const navigate = useNavigate();
    const [items, setItems] = React.useState(null);
    React.useEffect(() => {
        let cancelled = false;
        api.get(`/discipline/${recordId}/related-incidents`)
            .then((r) => { if (!cancelled) setItems(r.data?.items || []); })
            .catch(() => { if (!cancelled) setItems([]); });
        return () => { cancelled = true; };
    }, [recordId]);
    if (items === null) {
        return <div className="text-xs text-[color:var(--asl-muted)] mt-4">Loading related incidents…</div>;
    }
    if (items.length === 0) return null;
    return (
        <div className="mt-5" data-testid="disc-related-incidents">
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)] flex items-center gap-2 mb-2">
                Related incidents
            </div>
            <ul className="space-y-1.5">
                {items.map((it) => {
                    const code = (it.module_code || "").toLowerCase();
                    return (
                        <li
                            key={it.id}
                            className="flex items-center gap-3 text-xs border rounded-md px-2 py-1.5"
                            style={{
                                borderColor: "var(--asl-border)",
                                borderLeft: it.red_flag ? "3px solid var(--asl-crimson, #A8324A)" : "3px solid transparent",
                                cursor: "pointer",
                            }}
                            onClick={() => code && navigate(`/m/${code}/incidents?open=${it.id}`)}
                            data-testid={`disc-related-incident-${it.id}`}
                        >
                            <span
                                className="mono uppercase font-bold px-1.5 py-0.5 rounded"
                                style={{
                                    background: "rgba(27, 42, 86, 0.08)",
                                    color: "var(--asl-navy)",
                                    fontSize: 10,
                                }}
                            >
                                {(it.module_code || "").toUpperCase()}
                            </span>
                            <span className="flex-1 min-w-0 truncate">
                                {(it.incident_type || "").replace(/_/g, " ")} · {(it.location || "—")}
                            </span>
                            {it.red_flag && (
                                <span
                                    className="uppercase font-bold px-1.5 py-0.5 rounded"
                                    style={{
                                        fontSize: 9,
                                        background: "rgba(168, 50, 74, 0.14)",
                                        color: "var(--asl-crimson)",
                                        border: "1px solid rgba(168, 50, 74, 0.28)",
                                    }}
                                >
                                    Red flag
                                </span>
                            )}
                            <span className="text-[10px] text-[color:var(--asl-muted)] whitespace-nowrap">
                                {(it.date_time || "").slice(0, 10)}
                            </span>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

